import { Button } from "@components/ui/Button"
import { NonIdealState } from "@components/ui/NonIdealState"
import { CartItem } from "@features/cart/CartItem"
import { InCartDonation } from "@features/cart/InCartDonation"
import { useCartState } from "@features/cart/useCartState"
import { FerveCartItem } from "@features/ferve/ferve-cart"
import { useFerve } from "@features/ferve/FerveProvider"
import { formatDollars } from "@utils/money-format"
import { cn } from "@utils/tw"
import { AnimatePresence, motion } from "motion/react"
import { ReactNode, useMemo } from "react"
import { useFlyingCarpet } from "../useFlyingCarpet"
import { trackEvent } from "@features/tracking/tracking"
import { Spinner } from "@components/ui/Spinner"
import { useAppData } from "eddev/hooks"

export function CartPanel() {
  const cart = useCartState()

  let contentsKey = ""
  let contents: ReactNode = null

  // console.log("C", cart.loading, cart.data?.isEmpty)
  if (cart.loading && cart.data?.isEmpty) {
    contentsKey = "loading"
    contents = (
      <div className="absolute inset-0 p-[--padding] flex flex-col">
        <NonIdealState icon={<Spinner className="mb-4" />} title="Loading your cart" description="Please wait..." />
      </div>
    )
  } else if (cart.data?.isEmpty) {
    contentsKey = "empty"
    contents = (
      <div className="absolute inset-0 p-[--padding] flex flex-col">
        <NonIdealState
          icon="cart"
          title="Your cart is empty"
          description="Add items to your cart to see them here."
          actions={
            <>
              <Button href="/program" variant="hero" shape="branded">
                View Program
              </Button>
            </>
          }
        />
      </div>
    )
  } else {
    contentsKey = "cart"
    contents = <CartDisplay />
  }

  return (
    <div className="absolute inset-0" data-mixpanel-area="Cart Panel">
      <AnimatePresence>
        <motion.div
          key={contentsKey}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className="absolute inset-0"
        >
          {contents}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

function CartDisplay() {
  const ferve = useFerve()
  const cart = useCartState()
  const carpet = useFlyingCarpet()

  let feeLabel = "Booking fee"
  const hasMerchandise = cart.data?.items.some((item) => item.isMerchandise)
  const hasNonMerchandise = cart.data?.items.some((item) => !item.isMerchandise && !item.isCartDonation)
  if (hasMerchandise && hasNonMerchandise) {
    feeLabel = "Booking + shipping"
  } else if (hasMerchandise) {
    feeLabel = "Shipping fee"
  }

  const groupedItems = useMemo(() => {
    const grouped = new Map<string, FerveCartItem[]>()
    cart.data?.items.forEach((item) => {
      if (item.isCartDonation) return
      const key = item.key
      if (!grouped.has(key)) {
        grouped.set(key, [])
      }
      grouped.get(key)!.push(item as FerveCartItem)
    })
    return [...grouped.values()]
  }, [cart.data?.items])

  return (
    <div className="absolute inset-0 flex flex-col">
      {/* Cart items */}
      <div className="p-[--padding] overflow-auto flex-1">
        <motion.div layoutRoot layoutScroll className="flex flex-col gap-5 md:gap-4 ">
          <AnimatePresence presenceAffectsLayout initial={false} mode="popLayout">
            {groupedItems?.map((item, i) => (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8, transition: { when: "beforeChildren" } }}
                key={item[0].key}
                layoutId={item[0].key}
                layout="position"
              >
                <CartItem items={item as FerveCartItem[]} />
              </motion.div>
            ))}
          </AnimatePresence>
          {ferve.cart.inCartDonationsEnabled && <InCartDonation />}
        </motion.div>
      </div>
      {/* Footer */}
      <div className="px-[--padding] py-4 border-t border-t-bg-low flex flex-col flex-none">
        <CartTotalRow
          label="Subtotal"
          value={formatDollars((cart.data?.totalValue ?? 0) - (cart.data?.fees ?? 0) || 0)}
        />
        <CartTotalRow label={feeLabel} value={formatDollars(cart.data?.fees || 0)} />
        <CartTotalRow label="Total" value={formatDollars(cart.data?.totalValue || 0)} variant="total" />
        <div className="flex gap-3 border-t border-t-bg-low pt-4">
          <Button className="flex-1" shape="branded" variant="primary" onClick={() => carpet.close()}>
            Close Cart
          </Button>
          <Button
            className="flex-1"
            shape="branded"
            variant="hero"
            loading={cart.loading}
            onClick={() => {
              trackEvent(
                "Checkout",
                {
                  TotalItems: cart.data?.items.length,
                  TotalValue: cart.data?.totalValueWithoutCartDonation,
                  Items: cart.data?.items
                    .filter((item) => !item.isCartDonation)
                    .map((item) => {
                      return {
                        Name: item.title,
                        Quantity: item.quantity,
                        PriceType: item.priceTypeLabel,
                        LoggedIn: ferve.isLoggedIn ? "Yes" : "No",
                        ItemType: item.isFlexipass
                          ? "Flexipass"
                          : item.isDonation
                            ? "Donation"
                            : item.isMerchandise
                              ? "Merchandise"
                              : "Ticket",
                      }
                    }),
                  InCartDonationSelection: cart.data?.inCartDonation?.mode,
                  InCartDonationAmount: cart.data?.inCartDonation?.amount,
                },
                null,
                {
                  send_immediately: true,
                  transport: "sendBeacon",
                },
              )
              document.location.href = ferve.url("/Cart/Checkout")
            }}
          >
            Checkout
          </Button>
        </div>
      </div>
    </div>
  )
}

function CartTotalRow(props: { label: string; value: string; variant?: "total" | "subtotal" }) {
  return (
    <div
      className={cn(
        "flex justify-between items-center my-[0.1em]",
        props.variant === "total" ? "type-title-s my-4 text-fg" : "type-ui-s my-1 text-fg-secondary",
      )}
    >
      <div>{props.label}</div>
      <div>{props.value}</div>
    </div>
  )
}
